import type {
  FilterItem,
  FilterSection,
  FilterValueCategory,
} from '../../models/search-result';

export type SidebarCategoryNode = {
  id: string;
  label: string;
  count: number;
  children: SidebarCategoryNode[];
  sections: FilterSection[];
};

export const getDefaultExpandedState = (
  sectionKey: string,
  valueCategoriesCollapsedByDefault: boolean,
): boolean =>
  sectionKey.startsWith('value-category:')
    ? !valueCategoriesCollapsedByDefault
    : true;

export const getSectionCount = (section: FilterSection): number =>
  section.count ||
  section.filters.reduce((total, item) => total + (item.count || 0), 0);

export const collectHierarchyStateKeys = (
  nodes: SidebarCategoryNode[],
): string[] => {
  const keys = new Set<string>();

  const visitValueCategory = (
    sectionId: string,
    category: FilterValueCategory,
  ) => {
    keys.add(`value-category:${sectionId}:${category.id}`);
    category.children?.forEach((child) => visitValueCategory(sectionId, child));
  };

  const visitNode = (node: SidebarCategoryNode) => {
    if (node.label) {
      keys.add(`category:${node.id}`);
    }

    node.sections.forEach((section) => {
      keys.add(section.id);
      section.valueCategories?.forEach((category) => {
        visitValueCategory(section.id, category);
      });
    });

    node.children.forEach((childNode) => visitNode(childNode));
  };

  nodes.forEach((node) => visitNode(node));

  return Array.from(keys);
};

export const buildCategoryTree = (
  sections: FilterSection[],
): SidebarCategoryNode[] => {
  const root: SidebarCategoryNode[] = [];

  sections.forEach((section) => {
    const path = section.categoryPath || [];

    if (!path.length) {
      root.push({
        id: `section-root-${section.id}`,
        label: '',
        count: getSectionCount(section),
        children: [],
        sections: [section],
      });
      return;
    }

    let currentLevel = root;
    let currentPath = '';

    path.forEach((label) => {
      currentPath = currentPath ? `${currentPath}>${label}` : label;
      let node = currentLevel.find((entry) => entry.id === currentPath);

      if (!node) {
        node = {
          id: currentPath,
          label,
          count: 0,
          children: [],
          sections: [],
        };
        currentLevel.push(node);
      }

      node.count += getSectionCount(section);
      currentLevel = node.children;
    });

    const leafNode = currentLevel.find(
      (entry) => entry.id === `${section.id}-leaf`,
    );
    if (leafNode) {
      leafNode.count += getSectionCount(section);
      leafNode.sections.push(section);
    } else {
      currentLevel.push({
        id: `${section.id}-leaf`,
        label: '',
        count: getSectionCount(section),
        children: [],
        sections: [section],
      });
    }
  });

  return root;
};

export const categoryContainsSelectedFilter = (
  sectionId: string,
  category: FilterValueCategory,
  isFilterChecked: (sectionId: string, filterItem: FilterItem) => boolean,
): boolean =>
  Boolean(category.filter && isFilterChecked(sectionId, category.filter)) ||
  category.filters.some((item) => isFilterChecked(sectionId, item)) ||
  Boolean(
    category.children?.some((childCategory) =>
      categoryContainsSelectedFilter(sectionId, childCategory, isFilterChecked),
    ),
  );

export const categoryHasSelectedDescendant = (
  sectionId: string,
  category: FilterValueCategory,
  isFilterChecked: (sectionId: string, filterItem: FilterItem) => boolean,
): boolean =>
  category.filters.some((item) => isFilterChecked(sectionId, item)) ||
  Boolean(
    category.children?.some((childCategory) =>
      categoryContainsSelectedFilter(sectionId, childCategory, isFilterChecked),
    ),
  );
